import React, { useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import {
  Layout,
  Seo,
  Cta,
  TabFeatures,
  HeaderContent,
  MainFeature,
  HowItWorks,
  OverflowAlternatingFeatures,
  BookADemoButton,
  Nav,
} from "../../components"
import Icon from "../../components/icon"
import mapping from "../../assets/videos/mapping1.mp4"
import mappingWeb from "../../assets/videos/mapping1.webm"
import table from "../../assets/videos/table.mp4"
import tableWeb from "../../assets/videos/table.webm"

import { useOnScreen } from "../../utils/hooks"

import {
  title,
  subtitle,
  description,
  features,
  pageTitle,
  tabs,
} from "../../data/emergency"

export default function Emergency() {
  const ref = useRef()
  const isHeaderVisible = useOnScreen(ref)
  return (
    <Layout>
      <Seo title={pageTitle} />
      <div>
        <Nav isHeaderVisible={isHeaderVisible} />
        <div className="relative bg-gray-900 overflow-hidden" ref={ref}>
          <div className="relative pt-6 pb-16 sm:pb-24 lg:pb-32">
            <HeaderContent
              title={{
                firstSentence: title,
                secondSentence: subtitle,
              }}
              description={
                <div className="sm:-mb-6">
                  {description.map(item => {
                    if (item.bold) {
                      return <h3 className="text-gray-50">{item.text}</h3>
                    } else {
                      return <p className="pb-6">{item.text}</p>
                    }
                  })}
                </div>
              }
              media={
                <StaticImage
                  src="../../assets/images/emergency/emergencyHeader.png"
                  alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                  placeholder="blurred"
                  blurredOptions=""
                  layout="constrained"
                  quality={100}
                  height={720}
                  loading="eager"
                  aspectRatio={16 / 9}
                  backgroundColor="transparent"
                  className="rounded-lg"
                />
              }
              features={features}
            />
          </div>
        </div>

        <MainFeature
          title={{ blackText: "One", blueText: "platform" }}
          subtitle="Are you ready to simplify your emergency lighting compliance?"
          imageLeft={
            <StaticImage
              src="../../assets/images/emergency/emergency-dashboard.png"
              alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
              placeholder="blurred"
              layout="constrained"
              quality={100}
              height={430}
              loading="eager"
              aspectRatio={16 / 9}
              backgroundColor="transparent"
              className="rounded-lg object-center object-cover"
            />
          }
          textLeft={
            <div className="description text-gray-500">
              <p>
                A single user interface to allow you to securely monitor your
                emergency installations across your entire estate from anywhere
                in the world.
              </p>
              <p className="mt-4">
                The intuitive user interface allows users to quickly derive the
                information they need to perform the tasks that keep their
                estate running smoothly without the overhead of other solutions.
              </p>
            </div>
          }
          imageRight={
            <StaticImage
              src="../../assets/images/emergency/emergency-mobile.png"
              alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
              placeholder="blurred"
              blurredOptions=""
              layout="constrained"
              quality={100}
              height={430}
              loading="eager"
              aspectRatio={16 / 9}
              backgroundColor="transparent"
              className="rounded-lg object-center object-cover"
            />
          }
          textRight={
            <>
              <p class="description text-gray-500">
                The <span className="text-gray-900 font-bold">push</span>
                <span className="text-pushfusion font-extrabold">
                  fusion
                </span>{" "}
                mobile app gives maintenance teams all the information they need
                to complete their visit successfully as they arrive onsite. With
                this power in their hand, they save time and cause less
                disruption for the client by getting their job done in one
                visit.
              </p>
              <div className="mt-8 flex">
                <div className="inline-flex rounded-md shadow">
                  <a
                    href="https://apps.apple.com/gb/app/pushfusion/id1600240007"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <StaticImage
                      src="../../assets/images/app-store-lg.png"
                      alt="apple app store logo"
                      placeholder="tracedSVG"
                      layout="constrained"
                      quality={100}
                      height={50}
                      loading="eager"
                    />
                  </a>
                </div>
                <div className="ml-3 inline-flex">
                  <a
                    href="https://play.google.com/store/apps/details?id=io.pushfusion.mobile"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <StaticImage
                      src="../../assets/images/google-play-lg.png"
                      alt="apple app store logo"
                      placeholder="tracedSVG"
                      layout="constrained"
                      quality={100}
                      height={50}
                      loading="eager"
                    />
                  </a>
                </div>
              </div>
            </>
          }
        />
        <HowItWorks
          title={{ firstSentence: "How it", secondSentence: "works" }}
          position="middle"
          description={
            <>
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">
                fusion
              </span>{" "}
              brings the monthly and annual emergency lighting test results from
              all your buildings together in one place.
            </>
          }
          image={
            <StaticImage
              src="../../assets/images/emergency/diagram.png"
              objectFit="cover"
              placeholder="tracedSVG"
              alt="pushfusion emergency lighting setup diagram"
              quality={100}
            />
          }
        />
      </div>
      <OverflowAlternatingFeatures
        title={{
          firstSentence: (
            <>
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">fusion</span>
            </>
          ),
          secondSentence: "dashboard",
        }}
        description={
          <span>
            Are your emergency lighting test reports reliable? Are you spending
            too much on site visits to gather compliance reports across all your
            buildings? <span className="text-gray-700 font-bold">push</span>
            <span className="text-pushfusion font-extrabold">fusion</span> can
            help you reduce costs and improve reporting confidence, get to know
            your compliance footprint at the click of a button.
          </span>
        }
        data={[
          {
            showButton: false,
            icon: (
              <Icon
                className="h-16 w-16 text-white"
                aria-hidden="true"
                color="#0E98FA"
                icon="map2"
              />
            ),
            title: "Mapping functions and visualisation",
            content: (
              <>
                <p>
                  <span className="text-pushfusion-secondary font-bold">
                    push
                  </span>
                  <span className="text-pushfusion font-extrabold">
                    fusion’s
                  </span>{" "}
                  intuitive map shows each of your emergency installations in
                  one place. Every site is represented by an icon showing the
                  current state, allowing you to quickly and easily identify
                  installations in need of attention. Need some more
                  information? Click on the icon to get a helpful building
                  summary.
                </p>
                <p>
                  Plus, you control what is displayed; only interested in the
                  failures? Then{" "}
                  <span className="text-pushfusion-secondary font-bold">
                    push
                  </span>
                  <span className="text-pushfusion font-extrabold">fusion</span>{" "}
                  will only display those details, it’s all as easy as flicking
                  a switch.
                </p>
              </>
            ),
            image: (
              <div className="relative">
                <video
                  height="560"
                  autoPlay
                  id="vid2"
                  loop
                  muted
                  className="rounded-xl shadow-xl"
                >
                  <source src={mappingWeb} type="video/webm" />
                  <source src={mapping} type="video/mp4" />
                </video>
              </div>
            ),
          },
          {
            showButton: true,
            icon: (
              <Icon
                className="h-16 w-16 text-white"
                aria-hidden="true"
                color="#0E98FA"
                icon="table"
              />
            ),
            title: "Emergency simplified for your entire estate",
            content: (
              <p>
                Prefer more detail? You can view the summary of the emergency
                report data as a table. Letting you filter and sort the data by
                any of the fields allowing to drill into the information you
                need quickly and with minimum fuss.
              </p>
            ),
            image: (
              <div className="relative">
                <video
                  width="640"
                  autoPlay
                  id="vid2"
                  loop
                  muted
                  className="rounded-xl shadow-xl"
                >
                  <source src={tableWeb} type="video/webm" />
                  <source src={table} type="video/mp4" />
                </video>
              </div>
            ),
          },
          {
            showButton: false,
            icon: (
              <Icon
                className="h-16 w-16 text-blue-50"
                aria-hidden="true"
                color="#0E98FA"
                icon="investigation"
              />
            ),
            title: "Test detail for each building",
            content: (
              <p>
                <span className="text-pushfusion-secondary font-bold">
                  push
                </span>
                <span className="text-pushfusion font-extrabold">fusion</span>{" "}
                holds the historical test reports for all your buildings in one
                place. So, in the event you need to query a report from last
                month or last year, you can access the information from anywhere
                you have an Internet connection.
              </p>
            ),
            image: (
              <StaticImage
                src="../../assets/images/emergency/testBook2.png"
                alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                placeholder="blurred"
                layout="constrained"
                quality={100}
                height={446}
                loading="eager"
                aspectRatio={16 / 9}
                backgroundColor="transparent"
                className="h-full rounded-xl shadow-xl"
              />
            ),
          },
        ]}
      />
      <TabFeatures
        data={tabs}
        title={{ firstSentence: "pushfusion", secondSentence: "mobile app" }}
        description={
          <div className="text-justify">
            We designed the mobile app for onsite engineers to revolutionise the
            emergency lighting maintenance workflow for your entire
            organisation. It works seamlessly with the{" "}
            <span className="text-gray-700 font-bold"> push</span>
            <span className="text-pushfusion font-extrabold">fusion</span>{" "}
            emergency lighting module to harmonise activities onsite with
            processes in the back office. Put simply, it strengthens your field
            teams and empowers them to resolve compliance and maintenance issues
            with minimum effort and maximum efficiency.
          </div>
        }
      />
      <Cta
        title="Find out how pushfusion can help you report on your"
        subtitle="emergency lighting compliance with confidence."
        button1={
          <BookADemoButton
            background="bg-gray-50"
            backgroundHover="bg-gray-300"
            textColor="text-pushfusion"
          />
        }
      />
    </Layout>
  )
}
